<template>
  <div class="sm-plan-select">
    <div class="sm-wrap lg">
      <div class="sm-plan-select-inner">
        <slot name="header" />
        <div class="sm-plan-select__btns">
          <button
            v-for="(item, index) in planTypes"
            :key="index"
            :class="item == planType ? 'active' : ''"
            @click="setPlanType(item)"
            v-if="item === 'personal' ? hasPersonalPLans : true"
          >
            <span>{{item.replace(/_/g, '')}}</span>
          </button>
        </div>
        <div class="sm-plan-select__btns">
          <button
            v-for="(item, index) in periods"
            :key="index"
            :class="item == period ? 'active' : ''"
            @click="setPeriod(item)"
          >
            <span>{{item.replace(/_/g, '')}}</span>
          </button>
        </div>
        <div
          class="sm-plan-select__plans"
          :class="{'four': plansWithPeriod.length >= 4}"
        >
          <div
            v-for="(plan, index) in plansWithPeriod"
            :key="plan.name"
            class="sm-plan-select__plans-item"
            :class="{
              'popular': currentPlanId ? plan.id == currentPlanId : index == 1,
              'phone-only': !plan.mail_service_enabled
            }"
          >
            <span
              v-if="showHidden && !plan.is_active"
              class="sm-txt-center sm-small disabled is-hidden"
            >Hidden Plan</span>
            <h4 :class="{'long-title': plan.title && plan.title.length > 30}">{{plan.title}}</h4>
            <p
              v-if="showDescription"
              class="sm-txt-center"
            >{{plan.description}}</p>
            <div class="sm-plan-select__plans-item-price">
              <template v-if="period == 'monthly'">
                <strong>{{formatPrice(plan.subscription_price)}}</strong>
                <span>/month</span>
              </template>
              <template v-else>
                <strong
                  v-if="period == 'yearly'"
                  class="center"
                >{{formatPrice(plan.period_amount / 12)}}</strong>
                <strong
                  v-else
                  class="center"
                >{{formatPrice(plan.period_amount / 6)}}</strong>
                <span>/month</span>
              </template>
            </div>
            <span class="sm-txt-center sm-small disabled sm-plan-select__plans-item-unit">
              <template v-if="period != 'monthly'">
                {{formatPrice(plan.period_amount)}} billed {{period.replace(/_/g, '')}}
              </template>
            </span>
            <btn-block
              v-if="!currentPlanId"
              :text="btnText"
              :type="index != 1 ? 'white' : 'base'"
              @click="selectPlan(plan)"
            />
            <btn-block
              v-else
              :class="{
                'disabled': plan.id == currentPlanId && period == currentPlanTerm
              }"
              :text="getBtnText(plan, index)"
              :type="plan.id == currentPlanId ? 'base' : 'white'"
              @click="selectPlan(plan)"
            />
            <ul class="sm-plan-select__plans-item-list">
              <li
                v-for="feature in plan.features"
                :key="feature.text"
                :class="feature.checked ? 'checked' : ''"
                v-html="feature.text"
              ></li>
            </ul>
            <a
              v-if="plan.moreFeatures.length"
              href="#"
              class="sm-blue-link"
              @click.prevent="showPopup(plan)"
            >View more</a>
          </div>
        </div>
        <!-- <a href="#" class="sm-h6 sm-txt-center">
          <span>Compare plans</span>
          <include-svg :name="'icon-arrow-b-full'" />
        </a> -->
      </div>
    </div>

    <popup-block
      v-if="popup.isOpen"
      :open="popup.isOpen"
      :size="'sm-popup-medium'"
      class="sm-plan-select__popup"
      @close="popup.isOpen = false;"
    >
      <template slot="popupContent">
        <div class="sm-plan-select__popup-content">
          <h4>Additional services</h4>
          <ul class="sm-plan-select__plans-item-list">
            <li
              v-for="feature in popup.plan.moreFeatures"
              :key="feature.text"
              :class="feature.checked ? 'checked' : ''"
            >
              {{feature.text}}
            </li>
            <li
              v-for="feature in planCustomFeatures"
              :key="feature.paln_id+feature.id+feature.field_attr"
              class="checked"
            >{{`${feature.title} - ${formatPrice(feature.price) + (feature.term === 'monthly' ? '/month': '')}`}}</li>
          </ul>
          <div class="sm-plan-select__popup-content-footer">
            <include-svg :name="'icon-location'" />
            <p v-if="providerAddress.line_1">{{providerAddress.line_1}}</p>
            <p>
              <template v-if="providerAddress.line_2">{{providerAddress.line_2}}</template>
              <template v-if="providerAddress.line_3">, {{providerAddress.line_3}}</template>
            </p>
          </div>
        </div>
      </template>
    </popup-block>

    <sm-spinner :show="showSpinner" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import {FormatPrice} from '@/utils/general-data';
import PeriodsSettings from '@/settings/periods';
import {GetAllPlans} from '@/api/plans';
import Popup from '@/components/Popup';
import PlanFeatures from '@/settings/plan-features';
import ProviderAddress from '@/mixins/ProviderAddress';
import Spinner from '@/components/Spinner';
import Prices from '@/settings/prices';

export default {
  name: 'plan-select',
  components: {
    'popup-block': Popup,
    'sm-spinner': Spinner
  },
  props: {
    replaceLink: {
      type: String,
      default: ''
    },
    currentPlanId: {
      type: Number,
      default: null
    },
    currentPlanTerm: {
      type: String,
      default: ''
    },
    showHidden: {
      type: Boolean,
      default: false
    },
    btnText: {
      type: String,
      default: 'Get started'
    }
  },
  data() {
    return {
      periods: ['monthly'],
      period: 'monthly',
      periodAlts: PeriodsSettings.alts,
      planTypes:['business' , 'personal'],
      planType:'business',
      plans: [],
      customFeatures: [],
      planCustomFeatures: [],
      popup: {
        plan: null,
        isOpen: false
      },
      features: [
        'complimentary_mail_items',
        'complimentary_scans',
        'complimentary_shreds',
        'joint_account_price',
        'complimentary_conf_room_hours',

        'complimentary_days', //?

        // 'setup_fee',
        // 'complimentary_mail_items',
        // 'complimentary_scans',
        // 'joint_account_price',
        // 'complimentary_days',
        // 'complimentary_conf_room_hours'
      ],
      moreFeatures: [
        'price_per_mail_item',
        'price_per_scan',
        'price_per_shred',
        'storage_price',
        'forward_surcharge',
        'forward_package_surcharge', // new

        // 'price_per_mail_item',
        // 'price_per_scan',
        // 'price_per_shred',
        // 'forward_surcharge',
        // 'check_deposit_service_price',
        // 'storage_price',
      ],
      phoneFeatures: [
        'phone_price',
      ],
      hasPersonalPLans: false,
    }
  },
  created() {
    this.showSpinner = true;

    if (this.currentPlanTerm)
      this.setPeriod(this.currentPlanTerm);

    // if (this.$route.name == 'select-plan') {
    //   this.getPlans()
    //     .then((plans) => this.getPlansCallback(plans));
    // } else {
      GetAllPlans(this.showHidden)
        .then((res) => this.getPlansCallback(res.plans));
    // }
  },
  computed: {
    plansWithPeriod() {
      return this.plans.filter(plan => {
        if ((this.period == 'monthly' || this.period == plan.period_unit) && plan.plan_type === this.planType)
          return true;

        return false;
      });
    },
    showDescription() {
      return this.plans.some(plan => plan.description);
    },
    currentPlan() {
      if (this.currentPlanId && this.plans.length)
        return this.plans.find(plan => plan.id == this.currentPlanId);

      return null;
    },
    currentPlanIndex() {
      if (this.currentPlanId && this.plans.length)
        return this.plans.findIndex(plan => plan.id == this.currentPlanId);

      return null;
    }
  },
  mixins: [ProviderAddress],
  methods: {
    ...mapActions({
      getPlans: 'plans/getPlans',
      getCustomFeatures: 'general/getCustomFeatures',
    }),
    formatPrice: FormatPrice,
    getPlansCallback(plans) {
      this.$emit('plansReceived');

      this.hasPersonalPLans = plans.some((plan) => plan.plan_type === 'personal')

      this.plans = plans.sort((a, b) => {
        if (a.subscription_price > b.subscription_price)
          return 1;

        if (a.subscription_price < b.subscription_price)
          return -1;

        return 0;
      });

      this.filterFeaturesList();

      this.plans.forEach(plan => {
        plan.features = this.createFeaturesList(plan);
        plan.moreFeatures = this.createMoreFeatures(plan);

        if (plan.period_unit && !this.periods.includes(plan.period_unit)) {
          this.periods.push(plan.period_unit);

          if (!this.currentPlanTerm)
            this.period = plan.period_unit;
        }
      });

      if (this.plans.length && !this.plansWithPeriod.length) // To avoid customers with incorrect term
        this.period = 'monthly';

      this.showSpinner = false;
    },
    setPeriod(period) {
      this.period = period;
    },
    setPlanType(planType) {
      this.planType = planType;
    },
    selectPlan(plan) {
      if (this.replaceLink) {
        this.$router.push(this.replaceLink);
        return;
      }

      this.$emit('submit', {
        term: this.period,
        plan: plan
      });
    },
    filterFeaturesList() {
      this.features = this.features.filter(name => {
        let feature;

        Object.keys(PlanFeatures).some(key => {
          feature = PlanFeatures[key].find(item => item.field == name);

          if (feature)
            return true;
        });

        if (
          feature &&
          feature.available_field &&
          !this.plans.some(plan => !plan.hasOwnProperty(feature.available_field) || plan[feature.available_field])
        ) { // not show feature if available_field is false for all plans
          return false;
        }

        return true;
      });
    },
    createFeaturesList(plan) {
      let res = [];

      if (plan.mail_service_enabled) {
        this.features.forEach(name => {
          let item = this.addFeature(name, plan);

          if (item)
            res.push(item);
        });
      } else {
        this.phoneFeatures.forEach(name => {
          let item = this.addFeature(name, plan);

          if (item)
            res.push(item);
        });

        // TEMP value is hardcoded on backend!
        if (plan.has_live_receptionist)
          res.push({
            checked: true,
            text: `Live Answering Service (Optional) - from ${this.formatPrice(Prices.live_answering.from)}/month`
          });
      }

      return res;
    },
    createMoreFeatures(plan) {
      let res = [];

      if (plan.mail_service_enabled) {
        if (plan.phone_service_enabled) {
          [...this.moreFeatures, ...this.phoneFeatures].forEach(name => {
            let item = this.addFeature(name, plan, true);

            if (item)
              res.push(item);
          });

          // TEMP value is hardcoded on backend!
          if (plan.has_live_receptionist)
            res.push({
              checked: true,
              text: `Live Answering Service (Optional Add On) - from ${this.formatPrice(Prices.live_answering.from)}/month`
            });
        } else {
          this.moreFeatures.forEach(name => {
            let item = this.addFeature(name, plan, true);

            if (item)
              res.push(item);
          });
        }
      }

      return res;
    },
    addFeature(name, plan, isMore) {
      let feature = this.findFeature(name);

      if (!feature)
        return false;

      if(feature && feature.available_field === 'has_mail_forwarding' && !plan['has_mail_forwarding'] ){
          return false
      }


      if (!isMore && !this.featureAvailable(name, feature.ref_to))
        return false;

      if (isMore && !plan[feature.field])
        return false;

      if (
        (feature.hasOwnProperty('enable_field') && !plan[feature.enable_field]) ||
        (feature.hasOwnProperty('ref_from') && !plan[feature.ref_from])
      )
        return false;

      if (feature.pricing)
        Object.keys(feature.pricing).forEach(item => feature[item] = feature.pricing[item]);

      if (
        (plan[name] && (!feature.hasOwnProperty('available_field') || plan[feature.available_field])) ||
        feature.type == 'price'
      ) { // val
        if (feature.empty_title && plan[name] === 0)
          return {
            checked: true,
            text: feature.empty_title
          }
        else
          return {
            checked: true,
            text: `${feature.title}${feature.no_dash ? ' ' : ' - '}${feature.type == 'price' ? this.formatPrice(plan[name]) : plan[name] + (feature.type ? ' ' + feature.type : '')}${feature.suffix ? (feature.no_dash ? ' ' : '/') + feature.suffix : ''}${this.checkAdditionalData(feature, plan)}`
          };
      } else if (
        feature.hasOwnProperty('available_field') &&
        plan.hasOwnProperty(feature.available_field) &&
        !plan[feature.available_field] &&
        feature.empty_title
      ) { // disabled by available_field
        return {
          checked: false,
          text: feature.empty_title
        }
      } else if (feature.hasOwnProperty('ref_to')) {
        if (plan[feature.ref_to]) { // show ref_to
          let refFeature = this.findFeature(feature.ref_to);

          if (refFeature.pricing)
            Object.keys(refFeature.pricing).forEach(item => refFeature[item] = refFeature.pricing[item]);

          return {
            checked: true,
            text: `${refFeature.ref_title || refFeature.title}${feature.no_dash ? ' ' : ' - '}${this.formatPrice(plan[refFeature.field])}${refFeature.suffix ? (feature.no_dash ? ' ' : '/') + refFeature.suffix : ''}`
          };
        } else { // unlimited
          return {
            checked: true,
            text: `Unlimited ${feature.title}`
          };
        }
      } else { // ==0
        return {
          checked: false,
          text: `${(feature.disable_title || feature.title + ' is extra')}${this.checkAdditionalData(feature, plan)}`
        };
      }
    },
    featureAvailable(name, ref) {
      if (
        (name == 'complimentary_days' && this.plans.some(plan => plan.day_pass_discount_pct)) ||
        (name == 'complimentary_conf_room_hours' && this.plans.some(plan => plan.conference_rooms.length))
      )
        return true;

      return this.plans.some(plan => (plan.hasOwnProperty(name) && plan[name]) || (ref && plan.hasOwnProperty(ref) && plan[ref]));
    },
    findFeature(name) {
      let res;
      Object.keys(PlanFeatures).some(key => {
        res = PlanFeatures[key].find(item => item.field == name);

        if (res)
          return true;
      });

      return res;
    },
    showPopup(plan) {
      this.popup.plan = plan;
      this.popup.isOpen = true;
    },
    checkAdditionalData(feature, plan) {
      if (feature.field == 'complimentary_days')
        return this.checkDayPass(feature, plan);

      if (feature.field == 'complimentary_conf_room_hours')
        return this.checkConfRooms(feature, plan);

      return '';
    },
    checkDayPass(feature, plan) {
      let res = '';

      if (plan.day_pass_discount_pct) {
        res = `
          <span>
            <span>Day Pass Discount</span>
            <span></span>
            <span class="sm-red">${!plan.day_pass_discount_amount ? plan[feature.discount_field] + '%' : this.formatPrice(plan.day_pass_discount_pct)} OFF</span>
          </span>
        `;
      }

      return res;
    },
    checkConfRooms(feature, plan) {
      if (!plan.conference_rooms || !plan.conference_rooms.length)
        return '';

      let res = '';

      plan.conference_rooms.forEach((room, index) => {
        if (room.discount || room.discount_amount) {
          res += `
            <span>
              <span>${room.name}</span>
              <span></span>
              <span class="sm-red">${room.discount ? room.discount + '%' : this.formatPrice(room.discount_amount)} OFF</span>
            </span>
          `;
        }
      });

      return res;
    },
    getBtnText(plan, index) {
      if (plan.id == this.currentPlanId)
        return 'Current';

      if (this.currentPlanIndex > index)
        return 'Downgrade';

      return 'Upgrade';
    },
    getAllCustomFeatures() {
      if(this.plans.length) {
        const arr = this.plans.map(plan => this.getCustomFeatures(plan.id));

        Promise.all(arr)
        .then((resArr) => {
          if(resArr[0] && resArr[0].custom_features) {
            this.customFeatures = resArr.map(obj => obj.custom_features);
          }
        })
      }
    },
  },
  watch: {
    currentPlanTerm(val) {
      if (val)
        this.setPeriod(val);
    },
    plans() {
      this.getAllCustomFeatures();
    },
    popup: {
    deep: true,
    immediate: true,
    handler(val) {
      if(val.isOpen && val.plan && val.plan.id) {
        const features = this.customFeatures.find(el => el && el.length && el[0].plan_id === val.plan.id) || [];
        this.planCustomFeatures = features.filter(el => el.enabled && el.price > 0);
      } else this.planCustomFeatures = [];

    }
  }}
}
</script>

<style lang="scss">
  @use '@/assets/scss/general/mixins.scss' as *;
  @use '@/assets/scss/framework/scss-variables.scss' as *;
  @use '@/assets/scss/page-elements/_plan-select.scss';
</style>
